'use client';

/**
 * Browser wiring for invite-link.ts: one tracker per page lifetime, over this
 * browser's localStorage and the visitor's stored analytics choice, reporting
 * visits and claims through the two callables. Everything that decides
 * anything lives in invite-link.ts, where the tests can reach it.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { browserLocalStorage } from './browser-storage';
import { readConsent } from './cookie-consent';
import {
  CHECK_REFERRAL_CODE_CALLABLE,
  CLAIM_REFERRAL_CALLABLE,
  RECORD_REFERRAL_VISIT_CALLABLE,
  chooseClaim,
  claimWithRetry,
  createInviteTracker,
  fallbackClaim,
  type InviteTracker,
  type ReferralCodeCheck,
} from './invite-link';

let tracker: InviteTracker | null = null;

function inviteTracker(): InviteTracker {
  if (!tracker) {
    tracker = createInviteTracker({
      storage: browserLocalStorage(),
      consented: () => readConsent()?.analytics === true,
      recordVisit: (code) => {
        // Best-effort: a visit count is never worth an error on the page.
        httpsCallable(getFunctions(), RECORD_REFERRAL_VISIT_CALLABLE)({ code }).catch(() => {});
      },
      now: () => Date.now(),
    });
  }
  return tracker;
}

/** Pick up an ?invite= code from the current address's query string. Call on every page load and navigation. */
export function captureInviteFromSearch(search: string): void {
  if (typeof window === 'undefined') return;
  inviteTracker().capture(search);
}

/** Bring the device copy in line with a changed analytics choice. */
export function inviteConsentChanged(): void {
  if (typeof window === 'undefined') return;
  inviteTracker().consentChanged();
}

/**
 * Credit a just-created account to a referral link, if there is one: the code
 * typed into the signup page's Referral code box, else the link this tab or
 * device kept (chooseClaim), which is also tried if the server turns a typed
 * code down (fallbackClaim). Call the moment an account is created, while it
 * is signed in. Never rejects, and retries a failure that may pass on another
 * try. Once the server has answered, the device copy of the code has done its
 * job and goes.
 */
export async function claimInviteForNewAccount(typedCode?: string | null): Promise<void> {
  if (typeof window === 'undefined') return;
  const tracker = inviteTracker();
  const tracked = tracker.codeForClaim();
  const claim = httpsCallable(getFunctions(), CLAIM_REFERRAL_CALLABLE);
  // At most two claims: the one chosen, then, if it was a typed code the
  // server turned down, the link kept here (fallbackClaim).
  let sending = chooseClaim(typedCode, tracked);
  let answered = false;
  while (sending) {
    const current = sending;
    let claimed = false;
    answered = await claimWithRetry(async (c) => {
      const res = await claim({ code: c, via: current.via });
      claimed = (res.data as { claimed?: unknown } | null)?.claimed === true;
    }, current.code, {
      attempts: 3,
      delayMs: (attempt) => attempt * 1500,
      sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    });
    if (!answered) break;
    sending = fallbackClaim(current, tracked, claimed);
  }
  if (answered) tracker.claimAnswered();
}

/**
 * Whether a code typed into the signup page's Referral code box belongs to a
 * link that is switched on. Never rejects: a check that could not run is
 * "unchecked", which lets the signup go ahead, because the server judges the
 * code again when the account is opened.
 */
export async function checkReferralCode(code: string): Promise<ReferralCodeCheck['result']> {
  try {
    const res = await httpsCallable(getFunctions(), CHECK_REFERRAL_CODE_CALLABLE)({ code });
    return (res.data as { valid?: unknown } | null)?.valid === true ? 'valid' : 'invalid';
  } catch {
    return 'unchecked';
  }
}
